    /**
     * 세션 관련 에러 처리를 통합 관리하는 유틸리티
     */

    import { hasTokens } from './tokenValidator';

    /**
     * 로그인 페이지로 이동 (일관된 방식)
     */
        export const redirectToSignin = (clearStorage: boolean = true): void => {
        const isDev = process.env.REACT_APP_MODE === 'dev' || process.env.REACT_APP_RUN_MODE === 'dev';
        
        if (isDev) {
            return; // dev에서는 리다이렉트 안함
        }
        
        if (clearStorage) {
            localStorage.removeItem('access-token');
            localStorage.removeItem('refresh-token');
            localStorage.removeItem('access-exp');
            localStorage.removeItem('refresh-exp');
        }
        window.location.assign("/signin");
        };

    export type ErrorContext = 
    | 'app-startup' 
    | 'api-call' 
    | 'token-refresh' 
    | 'global-error' 
    | 'network-error'
    | 'interceptor-error';

    export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

    export interface SessionError {
    message: string;
    context: ErrorContext;
    severity: ErrorSeverity;
    originalError?: Error;
    shouldShowModal?: boolean;
    shouldRedirect?: boolean;
    }

    // 전역 세션 만료 모달 핸들러
    let showSessionExpiredModal: ((message?: string) => void) | null = null;

    export const setSessionErrorModalHandler = (handler: (message?: string) => void) => {
    showSessionExpiredModal = handler;
    };

    /**
     * 에러 컨텍스트별 메시지 생성
     */
    const getErrorMessage = (context: ErrorContext, originalError?: Error): string => {
    const baseMessages = {
        'app-startup': '앱 시작 중 세션에 문제가 발생했습니다.',
        'api-call': 'API 호출 중 세션이 만료되었습니다.',
        'token-refresh': '토큰 갱신에 실패했습니다.',
        'global-error': '예상치 못한 오류가 발생했습니다.',
        'network-error': '네트워크 연결에 문제가 발생했습니다.',
        'interceptor-error': '요청 처리 중 오류가 발생했습니다.'
    };

    const baseMessage = baseMessages[context];
    const errorDetail = originalError?.message ? ` (${originalError.message})` : '';
    
    return `${baseMessage} 다시 로그인해주세요.${errorDetail}`;
    };

    /**
     * 에러 심각도별 처리 방식 결정
     */
    const getErrorHandling = (severity: ErrorSeverity, context: ErrorContext) => {
    switch (severity) {
        case 'critical':
        return {
            shouldShowModal: true,
            shouldRedirect: true,
            shouldLog: true
        };
        case 'high':
        return {
            shouldShowModal: context !== 'app-startup', // 앱 시작 시에는 조용히 처리
            shouldRedirect: true,
            shouldLog: true
        };
        case 'medium':
        return {
            shouldShowModal: false,
            shouldRedirect: true,
            shouldLog: true
        };
        case 'low':
        return {
            shouldShowModal: false,
            shouldRedirect: false,
            shouldLog: false
        };
        default:
        return {
            shouldShowModal: false,
            shouldRedirect: true,
            shouldLog: true
        };
    }
    };

    /**
     * 통합 세션 에러 처리 함수
     */
    export const handleSessionError = (error: SessionError): void => {
    const { message, context, severity, originalError } = error;
    const handling = getErrorHandling(severity, context);
    
    // 로깅
    if (handling.shouldLog) {
        console.error(`[${context.toUpperCase()}] ${message}`, originalError);
    }
    
    // 모달 표시
    if (handling.shouldShowModal && showSessionExpiredModal) {
        showSessionErrorModal(message);
    }
    
    // 리다이렉트
    if (handling.shouldRedirect) {
        redirectToSignin();
    }
    };

    /**
     * 세션 만료 모달 표시 (기존 함수와 호환성 유지)
     */
    export const showSessionErrorModal = (message: string): void => {
    if (showSessionExpiredModal) {
        showSessionExpiredModal(message);
    } else {
        // fallback: 기본 confirm 사용
        const confirmed = window.confirm(message);
        if (confirmed) {
        redirectToSignin();
        }
    }
    };

    /**
     * 토큰 관련 에러 생성 헬퍼 함수들
     */
    export const createTokenError = (
    context: ErrorContext, 
    originalError?: Error
    ): SessionError => ({
    message: getErrorMessage(context, originalError),
    context,
    severity: 'high',
    originalError,
    shouldShowModal: context !== 'app-startup',
    shouldRedirect: true
    });

    export const createNetworkError = (
    context: ErrorContext,
    originalError?: Error
    ): SessionError => ({
    message: getErrorMessage(context, originalError),
    context,
    severity: 'medium',
    originalError,
    shouldShowModal: false,
    shouldRedirect: true
    });

    export const createCriticalError = (
    context: ErrorContext,
    originalError?: Error
    ): SessionError => ({
    message: getErrorMessage(context, originalError),
    context,
    severity: 'critical',
    originalError,
    shouldShowModal: true,
    shouldRedirect: true
    });

    /**
     * 토큰 존재 여부에 따른 에러 처리
     */
    export const handleTokenValidationError = (context: ErrorContext): void => {
    if (!hasTokens()) {
        const error = createTokenError(context);
        error.message = '토큰이 없습니다. 다시 로그인해주세요.';
        error.severity = 'medium';
        handleSessionError(error);
    }
    };

    /**
     * 401 에러 처리
     */
    export const handle401Error = (
    context: ErrorContext,
    originalError?: Error,
    shouldRetry: boolean = false
    ): void => {
    if (shouldRetry) {
        // 재시도 가능한 경우 - 모달 표시
        const error = createTokenError(context, originalError);
        handleSessionError(error);
    } else {
        // 재시도 불가능한 경우 - 조용히 리다이렉트
        const error = createTokenError(context, originalError);
        error.severity = 'medium';
        error.shouldShowModal = false;
        handleSessionError(error);
    }
    };

    /**
     * 네트워크 에러 처리
     */
    export const handleNetworkError = (
    context: ErrorContext,
    originalError?: Error
    ): void => {
    const error = createNetworkError(context, originalError);
    handleSessionError(error);
    };

    /**
     * 글로벌 에러 처리
     */
    export const handleGlobalError = (
    event: ErrorEvent | PromiseRejectionEvent,
    context: ErrorContext = 'global-error'
    ): void => {
    const error = event instanceof ErrorEvent ? event.error : event.reason;
    const isDev = process.env.REACT_APP_MODE === 'dev' || process.env.REACT_APP_RUN_MODE === 'dev';
    
    // ✅ dev 환경에서는 글로벌 에러를 세션 에러로 처리하지 않음
    if (isDev) {
      console.group('🔴 [DEV] 글로벌 에러 발생 (세션 에러로 처리 안함)');
      console.error('에러:', error);
      console.error('메시지:', error?.message);
      console.trace('호출 스택:');
      console.groupEnd();
      return; // dev에서는 여기서 종료
    }
    
    // ✅ 세션 관련 에러를 더 엄격하게 확인
    const isSessionRelated = 
      error?.response?.status === 401 ||  // HTTP 401 에러
      error?.status === 401 ||
      error?.code === 'ECONNABORTED' ||  // 타임아웃
      error?.message?.toLowerCase().includes('unauthorized') ||  // 인증 실패
      error?.message?.toLowerCase().includes('token') ||  // 토큰 관련
      error?.message?.toLowerCase().includes('session expired') ||  // 세션 만료
      error?.message?.toLowerCase().includes('authentication');  // 인증 관련
    
    if (isSessionRelated) {
      console.warn('⚠️ 세션 관련 에러 감지:', error?.message);
      event.preventDefault(); // 기본 에러 처리 방지
      const sessionError = createCriticalError(context, error);
      handleSessionError(sessionError);
    } else {
      // 세션과 무관한 에러는 그냥 로깅만
      console.error('일반 에러 발생 (세션 무관):', error);
    }
    };
